<template>
  <Teleport to="body">
    <Transition name="risk-result-fade">
      <div v-if="visible" class="risk-result-backdrop" role="presentation" @click.self="close">
        <div
          class="risk-result-card"
          role="dialog"
          aria-modal="true"
          aria-labelledby="risk-result-heading"
        >
          <canvas v-if="tierConfig.confetti" ref="confettiCanvas" class="risk-result-confetti" aria-hidden="true" />

          <button type="button" class="risk-result-close" aria-label="Fermer" @click="close">
            <XMarkIcon />
          </button>

          <!-- <div class="risk-result-icon" :class="`risk-result-icon--${tier}`" aria-hidden="true">
            {{ tierConfig.emoji }}
          </div> -->

          <p class="risk-result-eyebrow">Votre profil de risque investisseur</p>
          <h2 id="risk-result-heading" class="risk-result-title">{{ category?.label }}</h2>

          <div class="client-gauge risk-result-gauge" :style="{ '--client-gauge-value': scorePct }">
            <span class="client-gauge__value">{{ scorePct }}%</span>
          </div>

          <p v-for="item in category?.description || []" :key="item.id" class="risk-result-text">
            {{ item.text }}
          </p>

          <p v-if="horizonLabel" class="risk-result-meta">
            Horizon retenu : <strong>{{ horizonLabel }}</strong>
          </p>

          <button type="button" class="client-btn client-btn--primary risk-result-cta" @click="close">
            Continuer
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  visible: { type: Boolean, default: false },
  /** Entrée de CATEGORIES (profilInvest.js) : { label, min, max, description }. */
  category: { type: Object, default: null },
  scorePct: { type: Number, default: 0 },
  horizonLabel: { type: String, default: '' },
});
const emit = defineEmits(['close']);

/**
 * Intensité festive croissante avec l'appétence au risque du palier obtenu :
 * Conservateur/Prudent restent sobres (pulsation), Équilibré/Croissant/Dynamique
 * ont des confettis de plus en plus denses.
 */
const TIER_CONFIG = {
  calm: { emoji: '🛡️', confetti: false },
  balanced: { emoji: '⚖️', confetti: true, count: 60, power: 5, colors: ['#c9a227', '#01263e', '#7c9885'] },
  growth: { emoji: '📈', confetti: true, count: 90, power: 6.5, colors: ['#c9a227', '#16a34a', '#01263e', '#3b82f6'] },
  dynamic: {
    emoji: '🚀',
    confetti: true,
    count: 130,
    power: 8,
    colors: ['#c9a227', '#e11d48', '#3b82f6', '#16a34a', '#f59e0b'],
  },
};

function tierFromLabel(label) {
  const l = label || '';
  if (l.startsWith('Conservateur') || l.startsWith('Prudent')) return 'calm';
  if (l.startsWith('Équilibré')) return 'balanced';
  if (l.startsWith('Croissant')) return 'growth';
  return 'dynamic'; // Dynamique
}

const tier = computed(() => tierFromLabel(props.category?.label));
const tierConfig = computed(() => TIER_CONFIG[tier.value]);

const confettiCanvas = ref(null);
let stopConfetti = null;

function launchConfetti(canvas, { count, colors, power }) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

  const gravity = 0.16;
  const particles = Array.from({ length: count }, () => ({
    x: width / 2 + (Math.random() - 0.5) * 60,
    y: height * 0.12,
    vx: (Math.random() - 0.5) * power,
    vy: -Math.random() * power - 2,
    size: 4 + Math.random() * 5,
    color: colors[Math.floor(Math.random() * colors.length)],
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 12,
    life: 1,
    decay: 0.005 + Math.random() * 0.004,
  }));

  let raf;
  function tick() {
    ctx.clearRect(0, 0, width, height);
    let alive = false;
    for (const p of particles) {
      p.vy += gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;
      p.life -= p.decay;
      if (p.life > 0 && p.y < height + 20) {
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(p.life, 0);
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
    }
    if (alive) raf = requestAnimationFrame(tick);
  }
  tick();
  return () => cancelAnimationFrame(raf);
}

const AUTO_CLOSE_MS = 45_000;
let autoCloseTimer = null;

watch(
  () => props.visible,
  async (v) => {
    if (stopConfetti) {
      stopConfetti();
      stopConfetti = null;
    }
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
    if (v) {
      autoCloseTimer = setTimeout(close, AUTO_CLOSE_MS);
      if (tierConfig.value.confetti) {
        await nextTick();
        if (confettiCanvas.value) {
          stopConfetti = launchConfetti(confettiCanvas.value, tierConfig.value);
        }
      }
    }
  }
);

function close() {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer);
    autoCloseTimer = null;
  }
  emit('close');
}

onUnmounted(() => {
  if (autoCloseTimer) clearTimeout(autoCloseTimer);
  if (stopConfetti) stopConfetti();
});
</script>

<style scoped>
.risk-result-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  /* Fond flouté neutre : le formulaire reste visible (dépoli), sans teinte colorée dessus. */
  background: rgba(255, 255, 255, 0.35);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.risk-result-card {
  position: relative;
  width: 100%;
  max-width: 30rem;
  max-height: 90vh;
  overflow-y: auto;
  padding: var(--space-6) var(--space-5) var(--space-5);
  border-radius: var(--client-radius-lg, 24px);
  background: linear-gradient(160deg, #ffffff 0%, var(--client-surface-card, #f7f9fc) 100%);
  border: 1px solid rgba(201, 162, 39, 0.28);
  box-shadow: 0 24px 60px rgba(1, 38, 62, 0.28);
  text-align: center;
}

.risk-result-confetti {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  border-radius: inherit;
}

.risk-result-close {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: rgba(1, 38, 62, 0.06);
  color: var(--brand-navy);
  cursor: pointer;
}

.risk-result-close:hover {
  background: rgba(1, 38, 62, 0.12);
}

.risk-result-close svg {
  width: 18px;
  height: 18px;
}

.risk-result-icon {
  font-size: 2.75rem;
  line-height: 1;
  margin: 0 auto var(--space-3);
}

.risk-result-icon--calm {
  animation: risk-result-pulse 1.8s ease-in-out infinite;
}

@keyframes risk-result-pulse {
  0%, 100% {
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(201, 162, 39, 0.4));
  }
  50% {
    transform: scale(1.08);
    filter: drop-shadow(0 0 10px rgba(201, 162, 39, 0.35));
  }
}

@media (prefers-reduced-motion: reduce) {
  .risk-result-icon--calm {
    animation: none;
  }
}

.risk-result-eyebrow {
  font-family: var(--font-body);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--client-text-muted, #64748b);
  margin: 0 0 var(--space-2);
}

.risk-result-title {
  font-family: var(--font-display);
  font-size: 1.6rem;
  font-weight: 600;
  color: var(--brand-navy);
  margin: 0 0 var(--space-4);
}

.risk-result-gauge {
  margin: 0 auto var(--space-4);
}

.risk-result-text {
  font-family: var(--font-body);
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--client-text-muted, #64748b);
  margin: 0 0 var(--space-2);
  text-align: left;
}

.risk-result-meta {
  font-family: var(--font-body);
  font-size: 0.85rem;
  color: var(--client-text-muted, #64748b);
  margin: var(--space-2) 0 var(--space-4);
}

.risk-result-cta {
  margin-top: var(--space-2);
}

.risk-result-fade-enter-active,
.risk-result-fade-leave-active {
  transition: opacity 0.22s ease;
}

.risk-result-fade-enter-from,
.risk-result-fade-leave-to {
  opacity: 0;
}
</style>
